/**
 * ViolationFormBuilder - customize the member-facing violation submission form.
 */
import React, { useEffect, useState } from "react";
import {
  Box, Typography, TextField, Button, Paper, Snackbar, Alert,
  Grid, Switch, Chip, Tabs, Tab, Divider,
} from "@mui/material";
import SaveIcon       from "@mui/icons-material/Save";
import PreviewIcon    from "@mui/icons-material/Preview";
import LabelIcon      from "@mui/icons-material/Label";
import VisibilityIcon from "@mui/icons-material/Visibility";
import api from "../../utils/api";

const DEFAULT_CONFIG = {
  title:        "Add Violation",
  subtitle:     "Create a new HOA violation record and attach any supporting files.",
  submit_label: "Submit Violation",
  step_labels:  ["Property Info", "Violation Details", "Attachments", "Review"],
  fields: {
    account_id:    true,
    property_id:   true,
    violation_type: true,
    violation_date: true,
    comment:       true,
    attachments:   true,
  },
  field_labels: {
    account_id:     "Account ID",
    property_id:    "Property ID",
    violation_type: "Violation Type",
    violation_date: "Violation Date",
    comment:        "Comment",
    attachments:    "Supporting Files",
  },
};

const FIELD_GROUPS = [
  {
    step: "Step 1 - Property Info",
    fields: [
      { key: "account_id",   required: true },
      { key: "property_id",  required: true },
    ],
  },
  {
    step: "Step 2 - Violation Details",
    fields: [
      { key: "violation_type", required: true },
      { key: "violation_date" },
      { key: "comment" },
    ],
  },
  {
    step: "Step 3 - Attachments",
    fields: [
      { key: "attachments" },
    ],
  },
];

const cardSx  = { borderRadius:"14px", border:"1px solid rgba(47,99,173,0.08)", boxShadow:"0 4px 16px rgba(0,0,0,0.05)", p:2.5, mb:2.5 };
const fieldSx = { "& .MuiOutlinedInput-root":{ borderRadius:"10px" } };
const primaryBtnSx = {
  backgroundColor:"#2f63ad", color:"white", fontWeight:700,
  borderRadius:"10px", textTransform:"none", boxShadow:"none",
  "&:hover":{ backgroundColor:"#1f4f94", boxShadow:"none" },
};

const PreviewField = ({ label, required, type = "text" }) => (
  <Box sx={{ mb:1.25, height:36, borderRadius:"8px", border:"1px solid #d0d7e2",
    bgcolor:"white", display:"flex", alignItems:"center", px:1.5 }}>
    <Typography variant="caption" sx={{ color:"#9ca3af" }}>
      {label}{required ? " *" : ""}
    </Typography>
  </Box>
);

const ViolationFormBuilder = () => {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [saving, setSaving]  = useState(false);
  const [snack,  setSnack]   = useState({ open:false, msg:"", sev:"success" });
  const [tab,    setTab]     = useState(0);
  const [previewStep, setPreviewStep] = useState(0);

  useEffect(() => {
    api.get("/vio-form-config").then(r => {
      if (r.data && Object.keys(r.data).length > 0) {
        setConfig(prev => ({
          ...DEFAULT_CONFIG, ...r.data,
          fields:       { ...DEFAULT_CONFIG.fields,       ...(r.data.fields || {}) },
          field_labels: { ...DEFAULT_CONFIG.field_labels, ...(r.data.field_labels || {}) },
          step_labels:  r.data.step_labels || DEFAULT_CONFIG.step_labels,
        }));
      }
    }).catch(() => {});
  }, []);

  const setTop        = (k,v) => setConfig(c => ({ ...c, [k]: v }));
  const setFieldVis   = (k,v) => setConfig(c => ({ ...c, fields:       { ...c.fields,       [k]: v } }));
  const setFieldLabel = (k,v) => setConfig(c => ({ ...c, field_labels: { ...c.field_labels, [k]: v } }));
  const setStepLabel  = (i,v) => setConfig(c => { const a=[...c.step_labels]; a[i]=v; return {...c,step_labels:a}; });

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/vio-form-config", config);
      setSnack({ open:true, msg:"Violation form settings saved.", sev:"success" });
    } catch {
      setSnack({ open:true, msg:"Failed to save.", sev:"error" });
    } finally { setSaving(false); }
  };

  const f  = config.fields       || {};
  const fl = config.field_labels || {};

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", mb:3 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight:700, color:"#223142" }}>
            Violation Form Builder
          </Typography>
          <Typography variant="body2" sx={{ color:"#6b778c" }}>
            Customize labels, field visibility, and text for the violation submission form members see on the Owner Portal.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving} sx={primaryBtnSx}>
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </Box>

      {/* Tabs */}
      <Paper elevation={0} sx={{ borderRadius:"14px", border:"1px solid rgba(47,99,173,0.08)", mb:3, overflow:"hidden" }}>
        <Tabs value={tab} onChange={(_,v) => setTab(v)}
          sx={{ px:1, "& .MuiTab-root":{ textTransform:"none", fontWeight:600, minHeight:46 } }}>
          <Tab icon={<LabelIcon fontSize="small" />} iconPosition="start" label="Labels & Text" />
          <Tab icon={<VisibilityIcon fontSize="small" />} iconPosition="start" label="Field Visibility" />
          <Tab icon={<PreviewIcon fontSize="small" />} iconPosition="start" label="Preview" />
        </Tabs>
      </Paper>

      {/* ── Tab 0: Labels & Text ─────────────────────────────────────────── */}
      {tab === 0 && (<>
        <Paper elevation={0} sx={cardSx}>
          <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:2 }}>Form Header</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Form Title" value={config.title}
                onChange={e => setTop("title", e.target.value)} sx={fieldSx}
                helperText="Main heading shown at the top" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Submit Button Label" value={config.submit_label}
                onChange={e => setTop("submit_label", e.target.value)} sx={fieldSx} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Form Subtitle" value={config.subtitle}
                onChange={e => setTop("subtitle", e.target.value)} sx={fieldSx} />
            </Grid>
          </Grid>
        </Paper>

        <Paper elevation={0} sx={cardSx}>
          <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:2 }}>Step Tab Labels</Typography>
          <Grid container spacing={2}>
            {[0,1,2,3].map(i => (
              <Grid item xs={12} sm={3} key={i}>
                <TextField fullWidth size="small" label={`Step ${i+1}`}
                  value={config.step_labels?.[i] || ""} onChange={e => setStepLabel(i, e.target.value)} sx={fieldSx} />
              </Grid>
            ))}
          </Grid>
        </Paper>

        <Paper elevation={0} sx={cardSx}>
          <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:0.5 }}>Field Labels</Typography>
          <Typography variant="caption" sx={{ color:"#6b778c", display:"block", mb:2 }}>
            Rename any field to match your HOA's terminology.
          </Typography>
          <Grid container spacing={2}>
            {FIELD_GROUPS.map(group => (
              <Grid item xs={12} key={group.step}>
                <Typography variant="caption" sx={{ fontWeight:700, color:"#2f63ad", textTransform:"uppercase", letterSpacing:0.5, display:"block", mb:1 }}>
                  {group.step}
                </Typography>
                <Grid container spacing={1.5}>
                  {group.fields.map(field => (
                    <Grid item xs={12} sm={6} key={field.key}>
                      <TextField fullWidth size="small"
                        label={`"${DEFAULT_CONFIG.field_labels[field.key]}" label`}
                        value={fl[field.key] || ""}
                        onChange={e => setFieldLabel(field.key, e.target.value)}
                        sx={fieldSx}
                        InputProps={{
                          endAdornment: field.required ? (
                            <Chip label="Required" size="small"
                              sx={{ fontSize:9, height:16, bgcolor:"rgba(47,99,173,0.1)", color:"#2f63ad" }} />
                          ) : null,
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </>)}

      {/* ── Tab 1: Field Visibility ───────────────────────────────────────── */}
      {tab === 1 && (
        FIELD_GROUPS.map(group => (
          <Paper elevation={0} sx={cardSx} key={group.step}>
            <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:0.5 }}>
              {group.step}
            </Typography>
            <Typography variant="caption" sx={{ color:"#6b778c", display:"block", mb:2 }}>
              Toggle which fields appear. Required fields cannot be hidden.
            </Typography>
            <Grid container spacing={1}>
              {group.fields.map(field => (
                <Grid item xs={12} sm={6} key={field.key}>
                  <Box sx={{ display:"flex", alignItems:"center", justifyContent:"space-between",
                    px:1.5, py:1, borderRadius:"10px",
                    bgcolor: f[field.key] ? "rgba(47,99,173,0.04)" : "rgba(0,0,0,0.02)",
                    border:"1px solid rgba(47,99,173,0.08)" }}>
                    <Box sx={{ display:"flex", alignItems:"center", gap:1 }}>
                      <Typography variant="body2" sx={{ fontWeight:600, color:"#223142" }}>
                        {fl[field.key] || DEFAULT_CONFIG.field_labels[field.key]}
                      </Typography>
                      {field.required && (
                        <Chip label="Required" size="small"
                          sx={{ fontSize:10, height:18, bgcolor:"rgba(47,99,173,0.1)", color:"#2f63ad" }} />
                      )}
                    </Box>
                    <Switch checked={Boolean(f[field.key])} disabled={field.required}
                      onChange={e => setFieldVis(field.key, e.target.checked)} size="small" />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        ))
      )}

      {/* ── Tab 2: Preview ───────────────────────────────────────────────── */}
      {tab === 2 && (
        <Paper elevation={0} sx={{ ...cardSx, bgcolor:"rgba(47,99,173,0.015)" }}>
          <Box sx={{ display:"flex", alignItems:"center", justifyContent:"space-between", mb:2 }}>
            <Box sx={{ display:"flex", alignItems:"center", gap:1 }}>
              <PreviewIcon sx={{ color:"#2f63ad", fontSize:18 }} />
              <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142" }}>Form Preview</Typography>
            </Box>
            <Box sx={{ display:"flex", gap:0.5 }}>
              {[0,1,2,3].map(i => (
                <Button key={i} size="small" variant={previewStep===i?"contained":"outlined"}
                  onClick={() => setPreviewStep(i)}
                  sx={{ borderRadius:"8px", textTransform:"none", fontWeight:600, fontSize:11, minWidth:0, px:1.25,
                    ...(previewStep===i ? { bgcolor:"#2f63ad" } : { borderColor:"rgba(47,99,173,0.3)", color:"#2f63ad" }) }}>
                  {i+1}
                </Button>
              ))}
            </Box>
          </Box>

          <Box sx={{ border:"1px solid rgba(47,99,173,0.15)", borderRadius:"12px", p:2.5, bgcolor:"white" }}>
            <Typography variant="h6" sx={{ fontWeight:700, color:"#223142", mb:0.25 }}>{config.title}</Typography>
            <Typography variant="body2" sx={{ color:"#6b778c", mb:2 }}>{config.subtitle}</Typography>

            {/* Stepper */}
            <Box sx={{ display:"flex", gap:0, mb:2.5, borderRadius:"8px", overflow:"hidden", border:"1px solid rgba(47,99,173,0.12)" }}>
              {(config.step_labels || DEFAULT_CONFIG.step_labels).map((lbl, i) => (
                <Box key={i} sx={{ flex:1, py:0.75, textAlign:"center",
                  bgcolor: i===previewStep ? "#2f63ad" : i<previewStep ? "rgba(47,99,173,0.08)" : "white",
                  borderRight: i<3 ? "1px solid rgba(47,99,173,0.12)" : "none" }}>
                  <Typography variant="caption" sx={{ fontWeight:700, fontSize:11,
                    color: i===previewStep ? "white" : i<previewStep ? "#2f63ad" : "#9ca3af" }}>
                    {i+1}. {lbl}
                  </Typography>
                </Box>
              ))}
            </Box>

            {previewStep === 0 && (
              <Box>
                {f.account_id  && <PreviewField label={fl.account_id  || "Account ID"}  required />}
                {f.property_id && <PreviewField label={fl.property_id || "Property ID"} required />}
              </Box>
            )}
            {previewStep === 1 && (
              <Box>
                {f.violation_type && <PreviewField label={fl.violation_type || "Violation Type"} required />}
                {f.violation_date && <PreviewField label={fl.violation_date || "Violation Date"} />}
                {f.comment        && <PreviewField label={fl.comment        || "Comment"} />}
              </Box>
            )}
            {previewStep === 2 && (
              <Box>
                {f.attachments ? (
                  <Box sx={{ p:2, borderRadius:"8px", border:"2px dashed #90caf9", textAlign:"center", mb:1 }}>
                    <Typography variant="caption" sx={{ color:"#6b778c" }}>
                      {fl.attachments || "Supporting Files"} (upload)
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color:"#9ca3af" }}>No fields visible in this step.</Typography>
                )}
              </Box>
            )}
            {previewStep === 3 && (
              <Box>
                <Box sx={{ p:1.5, borderRadius:"8px", bgcolor:"rgba(47,99,173,0.04)", border:"1px solid rgba(47,99,173,0.1)" }}>
                  <Typography variant="caption" sx={{ color:"#6b778c" }}>Review summary of entered information...</Typography>
                </Box>
              </Box>
            )}

            <Box sx={{ display:"flex", justifyContent:"space-between", mt:2.5 }}>
              {previewStep > 0 && <Button size="small" variant="outlined" disabled sx={{ borderRadius:"8px", textTransform:"none" }}>Back</Button>}
              <Box sx={{ flex:1 }} />
              <Button size="small" variant="contained" disabled
                sx={{ borderRadius:"8px", textTransform:"none", fontWeight:700, bgcolor:"#2f63ad" }}>
                {previewStep < 3 ? "Next" : (config.submit_label || "Submit Violation")}
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      <Snackbar open={snack.open} autoHideDuration={3500}
        onClose={() => setSnack(s => ({...s,open:false}))}
        anchorOrigin={{ vertical:"top", horizontal:"center" }}>
        <Alert severity={snack.sev} onClose={() => setSnack(s => ({...s,open:false}))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ViolationFormBuilder;
